/**
 * «Rodar o robô agora» — pedido da Mariana, 01/10/2026 à noite: "deixa um botão lá para eu rodar o robô quando
 * eu quiser, inclusive dar comandos específicos". É o MESMO executar_fila.py do cron, com as opções escolhidas
 * aqui: conta, ações, limite por conta e modo simulado. Segura a trava do pipeline (nunca duas rodadas ao mesmo
 * tempo) e no fim relê a API, refaz a fila e republica o painel.
 */
import { useEffect, useState } from 'react'
import { Box, Button, Checkbox, FormControlLabel, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { estadoRobo, fetchFila, rodarRobo, type ResultadoRobo } from './promocoesService'
import { usePromo } from './ctx'
import { P, Panel } from './ui'

const ACOES: Array<{ k: string; rotulo: string; dica: string }> = [
  { k: 'entrar', rotulo: 'entrar', dica: 'candidatas que fecham a margem (regras 3, 4, 13 e 15)' },
  { k: 'trocar', rotulo: 'trocar', dica: 'vagas cheias: régua da regra 6' },
  { k: 'relampago', rotulo: 'relâmpago', dica: 'proposta do ML com margem ≥ piso, 5 unidades' },
  { k: 'cupom', rotulo: 'cupom', dica: '100% dos elegíveis, todas as campanhas, sem olhar margem' },
  { k: 'sair_auto', rotulo: 'sair sozinho', dica: 'ativa abaixo do piso de saída' },
]

export function RodarRobo() {
  const ctx = usePromo()
  const [contas, setContas] = useState<'ambas' | '777058595' | '3131364517'>('ambas')
  const [acoes, setAcoes] = useState<string[]>(ACOES.map((a) => a.k))
  const [max, setMax] = useState('')
  const [simular, setSimular] = useState(false)
  const [rodando, setRodando] = useState(false)
  const [msg, setMsg] = useState('')
  const [res, setRes] = useState<ResultadoRobo | null>(null)

  // se alguém já disparou (outra aba, outra pessoa), mostra o andamento
  useEffect(() => {
    let vivo = true
    void (async () => {
      try {
        const st = await estadoRobo()
        if (!vivo) return
        if (st.rodando) { setRodando(true); setMsg(`${st.passo ?? 'rodando'} (iniciado por ${st.por ?? '?'} às ${(st.iniciado_em ?? '').slice(11, 16)} UTC)`) }
        else if (st.resultado) setRes(st.resultado)
      } catch { /* serviço fora */ }
    })()
    return () => { vivo = false }
  }, [])

  const rodar = async () => {
    if (!acoes.length) { setMsg('escolha pelo menos uma ação'); return }
    const dry = simular || Boolean(ctx.live?.dry_run)
    const escreve = !dry
    if (escreve && !window.confirm(`O robô vai ESCREVER na API do Mercado Livre agora: ${acoes.join(', ')} · ${contas === 'ambas' ? 'as duas contas' : contas}${max ? ` · até ${max} por conta` : ' · sem limite'}. Confirma?`)) return
    setRodando(true); setRes(null); setMsg('iniciando…')
    try {
      const r = await rodarRobo(
        { contas: contas === 'ambas' ? undefined : [contas], acoes, max: max ? Number(max) : 0, dry_run: dry },
        (p) => setMsg(p + '…'),
      )
      setRes(r)
      setMsg(r.ok ? `terminou em ${Math.round(r.segundos / 60)} min` : `falhou: ${(r.saida || '').slice(-300)}`)
      if (r.ok && !r.opcoes.dry_run) {
        const f = await fetchFila()
        if (f) ctx.setFila(f)
        if (r.pos?.publicado) await ctx.recarregarBase()
      }
    } catch (e) { setMsg(`erro: ${e instanceof Error ? e.message : String(e)}`) }
    setRodando(false)
  }

  const resumo = res?.rodada?.contas?.map((c) => `${String(c.conta ?? '')}: ${String(c.executados ?? 0)} OK · ${String(c.pulados ?? 0)} reprovados · ${String(c.recusados ?? 0)} recusados pelo ML${c.erro ? ` · ERRO ${String(c.erro)}` : ''}`).join(' | ')

  return (
    <Panel title="🤖 Rodar o robô agora">
      <P>O mesmo robô do cron (06:15 / 12:15 / 18:15), na hora, com as opções abaixo. Ele segura a trava do pipeline (nunca duas rodadas ao mesmo tempo), executa a fila atual e no fim relê a API, refaz a fila e republica o painel. Com os cupons pode passar de 1 hora — o andamento fica aqui.</P>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField select size="small" label="Conta" value={contas} onChange={(e) => setContas(e.target.value as typeof contas)} sx={{ minWidth: 190 }}>
          <MenuItem value="ambas">as duas</MenuItem>
          <MenuItem value="777058595">Matriz · FIESTAPARTYFESTA</MenuItem>
          <MenuItem value="3131364517">Extrema · FIESTAPARTYHOME</MenuItem>
        </TextField>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {ACOES.map((a) => (
            <FormControlLabel key={a.k} title={a.dica} control={<Checkbox size="small" checked={acoes.includes(a.k)} onChange={(e) => setAcoes(e.target.checked ? [...acoes, a.k] : acoes.filter((x) => x !== a.k))} />} label={<span style={{ fontSize: 12.5 }}>{a.rotulo}</span>} />
          ))}
        </Box>
        <TextField size="small" label="Limite por conta" placeholder="todas" value={max} onChange={(e) => setMax(e.target.value.replace(/\D/g, ''))} sx={{ width: 140 }} title="0 ou vazio = sem limite. Use 50 para um teste." />
        <FormControlLabel control={<Checkbox size="small" checked={simular} onChange={(e) => setSimular(e.target.checked)} />} label={<span style={{ fontSize: 12.5 }}>simular (não escreve)</span>} />
        <Button size="small" variant="contained" color={simular ? 'inherit' : 'error'} disabled={!ctx.escreve || rodando} onClick={() => void rodar()}
          title={ctx.escreve ? '' : 'precisa do serviço no ar'}>{rodando ? 'rodando…' : simular ? '▶ Simular agora' : '▶ Rodar o robô agora'}</Button>
      </Stack>
      {msg ? <Typography sx={{ fontSize: 12.5, mt: 1 }}>{msg}</Typography> : null}
      {res ? (
        <Box sx={{ mt: 1, fontSize: 12.5 }}>
          <Typography sx={{ fontSize: 12.5 }}><b>{res.rodada?.dry_run ? 'SIMULADO' : 'escreveu na API'}</b> · {resumo || 'sem resumo (veja a saída)'} · tudo em Histórico.</Typography>
          {res.pos ? <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>depois: {res.pos.atualizacao ? (res.pos.atualizacao.ok ? 'API relida' : 'API NÃO relida') : 'sem releitura'} · motor {res.pos.ok ? 'ok' : 'falhou'} · painel {res.pos.publicado ? 'republicado' : 'NÃO republicado'}</Typography> : null}
          {!res.ok ? <Box component="pre" sx={{ fontSize: 11, whiteSpace: 'pre-wrap', maxHeight: 220, overflow: 'auto', bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>{res.saida}</Box> : null}
        </Box>
      ) : null}
    </Panel>
  )
}
